import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { DashboardShell } from '../components/brand/DashboardShell';
import {
  ArrowRightCircle, Users, LayoutGrid, FileCode2, ClipboardPaste, Sparkles,
  CheckCircle2, AlertCircle, Send, Loader2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// A small ready-made example so case managers can see what good input looks like.
const SAMPLE_BUNDLE = JSON.stringify({
  resourceType: 'Bundle',
  type: 'collection',
  entry: [
    {
      resource: {
        resourceType: 'Patient',
        id: 'pt-sample',
        name: [{ text: 'Jane Doe', given: ['Jane'], family: 'Doe' }],
        gender: 'female',
        birthDate: '1955-04-08',
        telecom: [{ system: 'phone', value: '+15551234567' }],
        address: [{ use: 'home', line: ['123 Maple Ave'], city: 'Fairfax', state: 'VA', postalCode: '22030' }],
      },
    },
    {
      resource: {
        resourceType: 'Encounter',
        id: 'enc-sample',
        status: 'finished',
        class: { code: 'IMP' },
        period: { start: '2026-02-18T08:00:00Z', end: '2026-02-22T14:30:00Z' },
        reasonCode: [{
          coding: [{ system: 'http://hl7.org/fhir/sid/icd-10-cm', code: 'I50.9', display: 'Heart failure, unspecified' }],
        }],
      },
    },
    {
      resource: {
        resourceType: 'Condition',
        code: { coding: [{ system: 'http://hl7.org/fhir/sid/icd-10-cm', code: 'I50.9', display: 'Heart failure, unspecified' }] },
      },
    },
    {
      resource: {
        resourceType: 'Condition',
        code: { coding: [{ system: 'http://hl7.org/fhir/sid/icd-10-cm', code: 'E11.9', display: 'Type 2 diabetes mellitus' }] },
      },
    },
  ],
}, null, 2);

export default function ReferralsFhirIngest() {
  const { getAuthHeader } = useAuth();
  const [rawFhir, setRawFhir] = useState('');
  const [sourceHint, setSourceHint] = useState('Epic');
  const [parsing, setParsing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [summary, setSummary] = useState(null);
  const [priority, setPriority] = useState('routine');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const navItems = [
    { icon: ArrowRightCircle, label: 'Referrals (FHIR ingest)', active: true },
    { icon: Users, label: 'Pipeline', href: '/planner/dashboard' },
    { icon: LayoutGrid, label: 'Operations', href: '/operations/executive' },
  ];

  const parseFhir = async () => {
    if (!rawFhir.trim()) {
      toast.error('Paste a FHIR Bundle or resource first.');
      return;
    }
    let parsed;
    try {
      parsed = JSON.parse(rawFhir);
    } catch (e) {
      toast.error('The pasted text is not valid JSON.');
      return;
    }
    setParsing(true);
    try {
      const { data } = await axios.post(
        `${API}/referrals/ingest-fhir`,
        { fhir: parsed, source_hint: sourceHint || null },
        getAuthHeader(),
      );
      setDraft(data.draft);
      setSummary(data.summary);
      toast.success('Referral draft pre-filled from FHIR.');
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      toast.error(`Could not parse FHIR: ${detail}`);
    } finally {
      setParsing(false);
    }
  };

  const saveReferral = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      await axios.post(
        `${API}/referrals/ingest-fhir/save`,
        { draft, priority, notes },
        getAuthHeader(),
      );
      toast.success('Referral saved to the pipeline.');
      setDraft(null);
      setSummary(null);
      setRawFhir('');
      setNotes('');
      setPriority('routine');
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      toast.error(`Could not save referral: ${detail}`);
    } finally {
      setSaving(false);
    }
  };

  const setField = (path, value) => {
    setDraft((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      const parts = path.split('.');
      let ref = next;
      for (let i = 0; i < parts.length - 1; i++) {
        ref[parts[i]] = ref[parts[i]] || {};
        ref = ref[parts[i]];
      }
      ref[parts[parts.length - 1]] = value;
      return next;
    });
  };

  return (
    <DashboardShell
      navItems={navItems}
      title="FHIR Encounter Ingest"
      subtitle="Paste a CCDA-equivalent FHIR Bundle from your EMR. We pre-fill the referral. Zero re-keying."
      testId="referrals-fhir-ingest-page"
    >
      <div className="grid lg:grid-cols-2 gap-6">
        {/* LEFT — Paste pane */}
        <div className="cn-card p-6 space-y-4" data-testid="fhir-paste-card">
          <div className="flex items-center gap-2">
            <ClipboardPaste className="w-4 h-4 text-cn-primary" />
            <h3 className="font-display font-semibold text-lg text-cn-text dark:text-white">Paste FHIR Bundle</h3>
          </div>
          <p className="text-xs text-cn-text-muted">
            Accepts a FHIR R4 <span className="font-mono">Bundle</span>, or a single
            <span className="font-mono"> Patient / Encounter / Condition</span> resource.
            From Epic or Cerner, export the patient&apos;s discharge encounter as FHIR and paste below.
          </p>

          <div>
            <Label htmlFor="source-hint" className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">
              EHR source
            </Label>
            <Input
              id="source-hint"
              value={sourceHint}
              onChange={(e) => setSourceHint(e.target.value)}
              placeholder="Epic, Cerner, MEDITECH…"
              className="mt-1"
              data-testid="fhir-source-hint"
            />
          </div>

          <div>
            <Label htmlFor="fhir-raw" className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">
              FHIR JSON
            </Label>
            <Textarea
              id="fhir-raw"
              value={rawFhir}
              onChange={(e) => setRawFhir(e.target.value)}
              placeholder='Paste a FHIR Bundle, e.g.: { "resourceType": "Bundle", "entry": [...] }'
              className="mt-1 font-mono text-xs min-h-[280px]"
              data-testid="fhir-raw-input"
            />
            <div className="flex justify-between items-center mt-2">
              <button
                type="button"
                className="text-xs text-cn-primary hover:underline"
                onClick={() => setRawFhir(SAMPLE_BUNDLE)}
                data-testid="fhir-load-sample"
              >
                Load sample bundle
              </button>
              <Button
                onClick={parseFhir}
                disabled={parsing || !rawFhir.trim()}
                className="cn-btn cn-btn-primary !py-2.5"
                data-testid="fhir-parse-btn"
              >
                {parsing
                  ? <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Parsing…</>
                  : <><Sparkles className="w-4 h-4 mr-1" /> Parse & pre-fill</>}
              </Button>
            </div>
          </div>
        </div>

        {/* RIGHT — Pre-filled referral draft */}
        <div className="cn-card p-6 space-y-4" data-testid="fhir-draft-card">
          <div className="flex items-center gap-2">
            <FileCode2 className="w-4 h-4 text-cn-primary" />
            <h3 className="font-display font-semibold text-lg text-cn-text dark:text-white">Pre-filled referral draft</h3>
          </div>

          {!draft && (
            <div className="text-sm text-cn-text-muted py-12 text-center" data-testid="fhir-draft-empty">
              <AlertCircle className="w-6 h-6 mx-auto mb-2 opacity-40" />
              Paste a FHIR Bundle and click &quot;Parse &amp; pre-fill&quot;.
            </div>
          )}

          {draft && (
            <>
              <div className="grid grid-cols-2 gap-2 text-xs" data-testid="fhir-draft-summary">
                <SummaryPill label="Patient" ok={summary?.patient_found} />
                <SummaryPill label="Encounter" ok={summary?.encounter_found} />
                <SummaryPill label="Conditions" ok={(summary?.condition_count || 0) > 0} count={summary?.condition_count} />
                <SummaryPill label="Resources" ok count={summary?.resource_count} />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] uppercase font-bold text-cn-text-muted">Patient name</Label>
                  <Input
                    value={draft.patient?.name || ''}
                    onChange={(e) => setField('patient.name', e.target.value)}
                    className="mt-1"
                    data-testid="fhir-draft-patient-name"
                  />
                </div>
                <div>
                  <Label className="text-[10px] uppercase font-bold text-cn-text-muted">DOB</Label>
                  <Input
                    value={draft.patient?.birth_date || ''}
                    onChange={(e) => setField('patient.birth_date', e.target.value)}
                    className="mt-1"
                    data-testid="fhir-draft-patient-dob"
                  />
                </div>
                <div>
                  <Label className="text-[10px] uppercase font-bold text-cn-text-muted">Primary diagnosis</Label>
                  <Input
                    value={draft.primary_diagnosis || ''}
                    onChange={(e) => setField('primary_diagnosis', e.target.value)}
                    className="mt-1"
                    data-testid="fhir-draft-primary-dx"
                  />
                </div>
                <div>
                  <Label className="text-[10px] uppercase font-bold text-cn-text-muted">ICD-10 code</Label>
                  <Input
                    value={draft.primary_diagnosis_code || ''}
                    onChange={(e) => setField('primary_diagnosis_code', e.target.value)}
                    className="mt-1"
                    data-testid="fhir-draft-primary-dx-code"
                  />
                </div>
                <div>
                  <Label className="text-[10px] uppercase font-bold text-cn-text-muted">Discharge date</Label>
                  <Input
                    value={draft.discharge_date || ''}
                    onChange={(e) => setField('discharge_date', e.target.value)}
                    className="mt-1"
                    data-testid="fhir-draft-discharge"
                  />
                </div>
                <div>
                  <Label className="text-[10px] uppercase font-bold text-cn-text-muted">Priority</Label>
                  <Select value={priority} onValueChange={setPriority}>
                    <SelectTrigger className="mt-1" data-testid="fhir-draft-priority">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="routine">Routine</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                      <SelectItem value="asap">ASAP</SelectItem>
                      <SelectItem value="stat">STAT</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {draft.secondary_diagnoses?.length > 0 && (
                <div>
                  <Label className="text-[10px] uppercase font-bold text-cn-text-muted">Secondary diagnoses</Label>
                  <ul className="mt-1 space-y-1 text-xs" data-testid="fhir-draft-secondary-dx-list">
                    {draft.secondary_diagnoses.map((d, i) => (
                      <li key={i} className="bg-cn-bone-100 dark:bg-cn-ink-800/50 rounded px-2 py-1">
                        <span className="font-mono font-bold mr-2">{d.code || '—'}</span>
                        {d.display}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div>
                <Label className="text-[10px] uppercase font-bold text-cn-text-muted">Notes for receiving provider</Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Anything not captured in the FHIR bundle — equipment needs, family concerns, etc."
                  className="mt-1 min-h-[80px]"
                  data-testid="fhir-draft-notes"
                />
              </div>

              <Button
                onClick={saveReferral}
                disabled={saving}
                className="cn-btn cn-btn-primary !py-2.5 w-full"
                data-testid="fhir-save-btn"
              >
                {saving
                  ? <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Saving…</>
                  : <><Send className="w-4 h-4 mr-1" /> Save & send to pipeline</>}
              </Button>
            </>
          )}
        </div>
      </div>
    </DashboardShell>
  );
}

const SummaryPill = ({ label, ok, count }) => (
  <div className={`rounded-md px-2 py-1.5 flex items-center justify-between ${ok ? 'bg-cn-sage-100/60 text-cn-primary' : 'bg-cn-bone-100 text-cn-text-muted'}`}>
    <span className="font-semibold">{label}</span>
    <span className="flex items-center gap-1">
      {typeof count === 'number' && <span className="font-mono">{count}</span>}
      {ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
    </span>
  </div>
);
